import { z } from 'zod';
import {
  dataHoraSchema,
  listagemDeCadastroSchema,
  respostaListaSchema,
  uuidSchema,
} from './comum.ts';
import { tributacaoIssSchema } from './fiscal.ts';

/**
 * Operação fiscal (`.spec/010`, Operação Fiscal; `.spec/001`, seção 2.2 (c)).
 *
 * É a **natureza da operação** da nota de compra: o cadastro que diz o que a
 * confirmação da nota deve fazer. Três decisões saem daqui, e nenhuma delas é
 * dedutível do CFOP sozinho:
 *
 * 1. o item movimenta estoque (e, se movimenta, sob qual motivo);
 * 2. a nota gera contas a pagar e atualiza o último custo;
 * 3. quais impostos destacados são **subtraídos** do custo de estoque.
 *
 * A avaliação é **item a item**, porque cada item da nota pode ter operação
 * fiscal diferente da operação padrão do cabeçalho (`.spec/010`).
 *
 * O cadastro **não calcula nada**. Ele classifica: quem soma, credita e
 * movimenta é a nota de compra, que lê estas flags no momento da confirmação e
 * congela o resultado (ADR 0004).
 *
 * ## A operação de saída é a da nota emitida
 *
 * As três decisões acima são da operação de **entrada**. A de saída responde
 * pela nota de serviço que a conta emite (`.spec/015`, decisões 90 e 91): gera
 * ou não o contas a receber, como o ISS incide e o que o tomador retém. Três
 * coisas daqui **não existem** nela, e a tela `CAD·13` não as mostra: os CFOPs
 * de origem do fornecedor (não há emitente a mapear), o consumo automático (é
 * o que a *entrada* significa) e os três créditos (crédito é de compra). E o
 * tipo de movimentação dela é de **saída** — o sentido do motivo acompanha o
 * da operação, regra que o service cobra por cruzar duas tabelas.
 *
 * ## Na saída o CFOP é opcional, e o sentido é declarado
 *
 * Desde a decisão 97 (`.spec/015`, 07/10/2026) a operação de saída pode ficar
 * **sem CFOP**: a NFS-e não leva CFOP, e exigir um obrigava a cadastrar 5933
 * numa operação que nenhum documento lê. A de entrada continua exigindo — é
 * pelo CFOP que a nota de compra e o XML a encontram. Sem CFOP não há primeiro
 * dígito de onde tirar o sentido, e ele passou a vir no payload — ver
 * `sentidoDaOperacao` e `problemaDoCfop`.
 */

/* -------------------------------------------------------------------------- */
/* CFOP                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Primeiro dígito do CFOP — de onde a operação vem ou para onde vai.
 *
 * Não é enum de domínio nosso: é a tabela oficial do Ajuste SINIEF. Fica aqui
 * como dado de rótulo porque é o que permite à tela dizer "entrada, mesmo
 * estado" em vez de exibir "1" e deixar o usuário decorar.
 */
export const ROTULO_GRUPO_CFOP: Readonly<Record<string, string>> = {
  '1': 'Entrada — mesmo estado',
  '2': 'Entrada — outro estado',
  '3': 'Entrada — exterior',
  '5': 'Saída — mesmo estado',
  '6': 'Saída — outro estado',
  '7': 'Saída — exterior',
};

/**
 * Quatro dígitos, com o primeiro entre os seis grupos válidos.
 *
 * A validação para no formato de propósito. Conferir o código **inteiro** contra
 * a tabela oficial exigiria embarcar e manter as cerca de setecentas linhas do
 * SINIEF, que mudam por ajuste — e um código legítimo recusado por catálogo
 * desatualizado trava a nota de quem está com o documento na mão. O grupo, esse
 * não muda desde 2003, e é o que pega o erro comum: digitar `556` ou `15561`.
 */
export const cfopSchema = z
  .string()
  .trim()
  .regex(/^[1-35-7]\d{3}$/, 'CFOP inválido: são 4 dígitos começando em 1, 2, 3, 5, 6 ou 7.');

/**
 * O CFOP da operação fiscal, **opcional** (`.spec/015`, decisão 97).
 *
 * Vazio, nulo e ausente são a mesma coisa — "sem CFOP" —, e saem `null`: o
 * campo de texto devolve `''` quando nada foi digitado, e a API não guarda
 * string vazia no lugar de ausência.
 *
 * A expressão é a de `cfopSchema` com o vazio aceito, e não uma união dos
 * dois: a união do Zod responde "entrada inválida" quando nenhum ramo casa, e
 * quem digitou `556` perderia a frase que diz quantos dígitos são.
 *
 * **Opcional aqui não é opcional sempre.** Quem decide se a operação pode
 * ficar sem ele é o sentido — ver `problemaDoCfop`.
 */
export const cfopOpcionalSchema = z
  .string()
  .trim()
  .regex(/^([1-35-7]\d{3})?$/, 'CFOP inválido: são 4 dígitos começando em 1, 2, 3, 5, 6 ou 7.')
  .nullish()
  .transform((valor) => (valor === undefined || valor === null || valor === '' ? null : valor));

/** O CFOP é de entrada (grupos 1, 2 e 3) — compra, e não devolução ao fornecedor. */
export function cfopDeEntrada(cfop: string): boolean {
  const grupo = cfop.charAt(0);
  return grupo === '1' || grupo === '2' || grupo === '3';
}

/* -------------------------------------------------------------------------- */
/* CFOP de origem — o de‑para do XML                                          */
/* -------------------------------------------------------------------------- */

/**
 * Um CFOP **do emitente**, sempre de saída.
 *
 * A origem é o que o fornecedor emitiu, e o que ele emite é saída. Aceitar um
 * `1556` aqui produziria uma linha que nunca casa — o arquivo não traz CFOP de
 * entrada —, e o sintoma seria "a tela não sugere nada", que manda procurar no
 * lugar errado.
 */
export const cfopDeOrigemSchema = z
  .string()
  .trim()
  .regex(/^[5-7]\d{3}$/, 'CFOP de origem inválido: são 4 dígitos começando em 5, 6 ou 7.');

/**
 * A lista de CFOPs de origem de uma operação de entrada.
 *
 * **Trinta é teto de sanidade, não regra fiscal.** A maior linha do catálogo tem
 * oito; quem passar de trinta quase certamente colou a tabela do SINIEF inteira
 * num campo, e o limite transforma isso em erro em vez de num cadastro que
 * ninguém consegue mais ler.
 */
export const cfopsOrigemSchema = z
  .array(cfopDeOrigemSchema)
  .max(30, 'São no máximo 30 CFOPs de origem por operação.')
  .prefault([]);

/** Há o mesmo CFOP duas vezes na lista? */
export function temOrigemRepetida(cfops: readonly string[]): boolean {
  return new Set(cfops.map((c) => c.trim())).size !== cfops.length;
}

/* -------------------------------------------------------------------------- */
/* Catálogo padrão de CFOP de entrada                                         */
/* -------------------------------------------------------------------------- */

/**
 * O pedido de importação do catálogo (`.spec/015`, decisão 21).
 *
 * `completo` distingue as duas portas: falso instala só as que uma construtora
 * usa na primeira semana — uso e consumo, com e sem ST, serviço e comodato —, e
 * verdadeiro traz as quinze, incluindo revenda, ativo imobilizado, transporte e
 * as devoluções.
 *
 * O padrão é **falso**, pelo motivo do catálogo curto: uma lista que o usuário
 * não escolheu o obriga a caçar a operação certa, e o erro dele é escolher pela
 * primeira que se parece — num campo que decide crédito de imposto e movimento
 * de estoque.
 */
export const importarCfopsPadraoSchema = z.object({
  completo: z.boolean().prefault(false),

  /**
   * Completa os **CFOPs de origem** das operações que já existem.
   *
   * A importação nunca toca no que já está cadastrado, e essa regra é o que
   * impede uma operação editada pelo contador de voltar ao padrão a cada
   * execução. Mas ela deixa de fora um caso legítimo: a conta provisionada
   * **antes** de o de‑para existir tem as operações certas e a lista de origens
   * vazia, e sem esta opção não haveria como preenchê-la senão à mão, código por
   * código.
   *
   * Por isso é opção, e não comportamento: ligada, ela **acrescenta** os códigos
   * do catálogo que faltam — nunca remove nem substitui os que o cliente pôs. O
   * preço é reconhecido e dito na tela: um código apagado de propósito volta.
   *
   * O casamento é pelo par **CFOP + nome**, o mesmo da idempotência das
   * operações. Casar só pelo CFOP alcançaria a operação renomeada, e também duas
   * operações homônimas de naturezas diferentes no mesmo código — criando na
   * conferência do XML uma ambiguidade que ninguém pediu.
   */
  completarOrigens: z.boolean().prefault(false),
});

export type ImportarCfopsPadrao = z.infer<typeof importarCfopsPadraoSchema>;

/**
 * O que a importação fez.
 *
 * `criadas` conta **operações**, e não linhas do catálogo: cada CFOP vira duas —
 * a de dentro e a de fora do estado. Repetir a importação devolve `criadas: 0`,
 * porque ela é idempotente pelo par CFOP + nome e nunca atualiza o que já existe.
 */
export const resultadoImportacaoCfopsSchema = z.object({
  criadas: z.number().int(),
  total: z.number().int(),
  /**
   * Quantos CFOPs de origem entraram em operações que **já existiam**.
   *
   * Separado de `criadas` porque as duas respondem a perguntas diferentes, e a
   * segunda é a que explica o caso em que nada foi criado e mesmo assim algo
   * mudou: "0 operações criadas, 34 CFOPs de origem acrescentados" é exatamente o
   * retorno de quem reimportou para completar o de‑para.
   */
  origensAcrescentadas: z.number().int(),
});

export type ResultadoImportacaoCfops = z.infer<typeof resultadoImportacaoCfopsSchema>;

/* -------------------------------------------------------------------------- */
/* Sentido                                                                    */
/* -------------------------------------------------------------------------- */

export const SENTIDOS_OPERACAO = ['ENTRADA', 'SAIDA'] as const;
export const sentidoOperacaoSchema = z.enum(SENTIDOS_OPERACAO);
export type SentidoOperacao = z.infer<typeof sentidoOperacaoSchema>;

export const ROTULO_SENTIDO_OPERACAO: Readonly<Record<SentidoOperacao, string>> = {
  ENTRADA: 'Entrada',
  SAIDA: 'Saída',
};

/**
 * O sentido a partir do CFOP. Grupos 1, 2 e 3 entram; 5, 6 e 7 saem.
 *
 * **É derivação, e não escolha do usuário**, e por isso `sentido` não está no
 * payload: o primeiro dígito do CFOP é a regra inteira, e um campo editável
 * abriria a chance de a operação dizer "entrada" com um CFOP de saída. O banco
 * grava o valor e um `CHECK` cobra a coerência.
 *
 * **Por que gravar, então, se dá para derivar na leitura.** Filtrar "só as de
 * saída" numa coluna indexável é mais barato que um `left(cfop, 1) IN (…)` em
 * toda consulta — e é esse o recorte do seletor de operação da nota de serviço.
 *
 * Este comentário previa uma segunda razão, e ela **não se confirmou**: "quando
 * a emissão de nota de serviço entrar, `cfop` deixa de ser obrigatório". A
 * emissão entrou em 06/10/2026 (`.spec/015`, decisão 90) usando a operação de
 * **saída com CFOP** — o Adriano definiu que a NFS-e usa o mesmo cadastro da
 * nota de compra, de sentido Saída —, e a coluna continua obrigatória. A NFS-e
 * não leva o CFOP; ele fica na operação como a classificação que ela já tinha.
 *
 * **Revisto em 07/10/2026 (decisão 97).** O Adriano definiu o contrário do
 * parágrafo acima: na operação de **saída** o CFOP é opcional. Com CFOP, tudo
 * o que está escrito aqui continua valendo — o primeiro dígito manda, e o
 * `CHECK` cobra. Sem CFOP não há dígito, e o sentido é o **declarado** no
 * payload; esta função continua sendo a regra do CFOP, e quem junta as duas
 * fontes é `sentidoDaOperacao`.
 *
 * Devolve `null` para código fora dos seis grupos. O `cfopSchema` já os recusa;
 * a queda existe para quem chama sobre texto ainda não validado.
 */
export function sentidoDoCfop(cfop: string): SentidoOperacao | null {
  const grupo = cfop.trim().charAt(0);

  if (grupo === '1' || grupo === '2' || grupo === '3') return 'ENTRADA';
  if (grupo === '5' || grupo === '6' || grupo === '7') return 'SAIDA';

  return null;
}

/** O que a operação diz de si para se saber o sentido dela. */
export interface CfopESentido {
  cfop: string | null;
  /** O sentido declarado. Só decide quando não há CFOP. */
  sentido?: SentidoOperacao | undefined;
}

/**
 * O sentido da operação (`.spec/015`, decisão 97).
 *
 * **Com CFOP, quem manda é o CFOP** — o primeiro dígito, como sempre foi. O
 * sentido declarado ao lado não o contraria: se discordar, é erro
 * (`problemaDoCfop`), e não desempate.
 *
 * **Sem CFOP, vale o declarado.** É o caso da operação de serviço prestado: a
 * NFS-e não tem CFOP, e a operação diz "saída" por extenso.
 *
 * `null` quando não dá para saber: CFOP fora dos seis grupos, ou sem CFOP e
 * sem declaração. Os dois são recusados antes de gravar.
 */
export function sentidoDaOperacao(dados: CfopESentido): SentidoOperacao | null {
  if (dados.cfop !== null) return sentidoDoCfop(dados.cfop);

  return dados.sentido ?? null;
}

/** O que há de errado entre o CFOP e o sentido de uma operação. */
export type ProblemaDoCfop = 'CFOP_OBRIGATORIO_NA_ENTRADA' | 'CFOP_DE_OUTRO_SENTIDO';

/**
 * As duas recusas que o CFOP opcional trouxe (`.spec/015`, decisão 97).
 *
 * 1. **Sem CFOP, só a operação de saída.** A de entrada é achada pelo CFOP —
 *    na nota de compra, no XML do fornecedor, no catálogo —, e sem ele não há
 *    o que a identifique. Sem CFOP e sem sentido declarado cai aqui também: o
 *    que falta dizer é o CFOP, que é de onde o sentido sempre veio.
 * 2. **O CFOP não contraria o sentido declarado.** `1102` numa operação que
 *    se diz de saída é engano num dos dois campos, e gravar pelo CFOP em
 *    silêncio trocaria a tela inteira de quem escolheu "saída".
 *
 * Função do contrato porque as três camadas a usam: o `superRefine` do
 * payload, o service e o schema da tela. O `CHECK` do banco é a quarta.
 */
export function problemaDoCfop(dados: CfopESentido): ProblemaDoCfop | null {
  if (dados.cfop === null) {
    return dados.sentido === 'SAIDA' ? null : 'CFOP_OBRIGATORIO_NA_ENTRADA';
  }

  const doCfop = sentidoDoCfop(dados.cfop);
  const declarado = dados.sentido;

  if (declarado !== undefined && doCfop !== null && doCfop !== declarado) {
    return 'CFOP_DE_OUTRO_SENTIDO';
  }

  return null;
}

/** As duas recusas, por extenso — uma frase para a API e para a tela. */
export const MENSAGEM_DO_PROBLEMA_DO_CFOP: Readonly<Record<ProblemaDoCfop, string>> = {
  // Curtas de propósito: aparecem sob o campo do CFOP, que é estreito.
  CFOP_OBRIGATORIO_NA_ENTRADA: 'Informe o CFOP: só a operação de saída pode ficar sem ele.',
  CFOP_DE_OUTRO_SENTIDO:
    'O CFOP não combina com o sentido: 1, 2 e 3 são de entrada; 5, 6 e 7, de saída.',
};

/**
 * O CFOP equivalente na outra origem — dentro do estado ⇄ fora do estado.
 *
 * `1102` ⇄ `2102`, `5102` ⇄ `6102`: o sufixo é o mesmo, só o primeiro dígito
 * muda. Quem usa é a **importação do catálogo**, para gerar a operação de fora do
 * estado ao lado da de dentro, com os CFOPs de origem correspondentes.
 *
 * **A leitura do XML não precisa dela.** O emitente em outro estado já emite
 * `6102`, e não `5102` — o primeiro dígito do arquivo carrega a informação que
 * uma comparação de UF calcularia. O de‑para casa por igualdade exata.
 *
 * Os grupos 3 e 7 **não têm par**, e devolvem `null`: importação e exportação são
 * a própria origem, e não a variante interestadual de nada.
 */
export function cfopDaOutraOrigem(cfop: string): string | null {
  const limpo = cfop.trim();
  const par: Readonly<Record<string, string>> = { '1': '2', '2': '1', '5': '6', '6': '5' };
  const trocado = par[limpo.charAt(0)];

  return trocado === undefined ? null : `${trocado}${limpo.slice(1)}`;
}

/* -------------------------------------------------------------------------- */
/* Regras compartilhadas                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Operação que movimenta estoque precisa dizer **sob qual motivo**.
 *
 * A confirmação da nota grava uma linha em `movimentacoes_estoque`, e essa
 * tabela exige `tipo_movimentacao_estoque_id` — é por ele que o extrato do
 * depósito se lê. Sem o vínculo aqui, a confirmação teria de inventar um motivo
 * (adivinhar "entrada por nota" pelo nome é o que faz uma conta que renomeou o
 * cadastro perder a entrada em silêncio) ou pedi-lo ao usuário no meio da
 * confirmação, que é tarde: quem confirma a nota já não está pensando em
 * estoque.
 *
 * O caminho inverso é livre: operação que **não** movimenta estoque pode
 * apontar um tipo, e ele simplesmente não é usado. Recusá-lo obrigaria o
 * usuário a limpar o campo antes de desmarcar a caixa, e o campo em branco não
 * conserta nada.
 *
 * **O sentido do tipo não se confere aqui** — ver
 * `tipoMovimentacaoNoSentidoDaOperacao`, logo abaixo.
 */
export function tipoMovimentacaoCoerente(entrada: {
  atualizaEstoque: boolean;
  tipoMovimentacaoEstoqueId: string | null;
}): boolean {
  return !entrada.atualizaEstoque || entrada.tipoMovimentacaoEstoqueId !== null;
}

/**
 * O tipo de movimentação tem o **sentido da operação**? (`.spec/015`, decisão 91)
 *
 * Entrada com entrada, saída com saída. Até 06/10/2026 a regra era "o tipo é
 * sempre de entrada", do tempo em que a operação fiscal só servia à nota de
 * compra; a operação de saída herdava um motivo que diz o contrário do que ela
 * faz.
 *
 * **Fora do `superRefine` do payload, e não por esquecimento:** o payload traz
 * o id do tipo, e o sentido dele mora na tabela de tipos de movimentação. Quem
 * tem as duas pontas é o service, que lê o tipo e chama esta função — a regra
 * está aqui para ter um lugar só e um teste, e a tela a antecipa oferecendo
 * apenas os tipos do sentido certo.
 *
 * `sentidoDoTipo` é `string`, e não o enum: é o que a coluna devolve, e um
 * valor fora da lista simplesmente não casa.
 *
 * Recebe o **sentido** da operação, e não mais o CFOP dela: desde a decisão 97
 * a operação de saída pode não ter CFOP, e quem sabe o sentido é
 * `sentidoDaOperacao`.
 */
export function tipoMovimentacaoNoSentidoDaOperacao(
  sentidoDaOperacaoFiscal: SentidoOperacao | null,
  sentidoDoTipo: string,
): boolean {
  return sentidoDaOperacaoFiscal !== null && sentidoDaOperacaoFiscal === sentidoDoTipo;
}

/** As seis marcas de retenção, na ordem em que a tela as mostra. */
export const CAMPOS_DE_RETENCAO = [
  'retemIss',
  'retemIrrf',
  'retemInss',
  'retemCsll',
  'retemPis',
  'retemCofins',
] as const;

export type CampoDeRetencao = (typeof CAMPOS_DE_RETENCAO)[number];

export const ROTULO_CAMPO_DE_RETENCAO: Readonly<Record<CampoDeRetencao, string>> = {
  retemIss: 'ISS retido pelo tomador',
  retemIrrf: 'IRRF',
  retemInss: 'INSS',
  retemCsll: 'CSLL',
  retemPis: 'PIS',
  retemCofins: 'COFINS',
};

/** A operação traz algum imposto de saída fora do padrão? */
export function impostosDeSaidaInformados(
  dados: { tributacaoIss: string } & Readonly<Record<CampoDeRetencao, boolean>>,
): boolean {
  return dados.tributacaoIss !== 'TRIBUTAVEL' || CAMPOS_DE_RETENCAO.some((campo) => dados[campo]);
}

/**
 * Dicas de formulário. Moram no contrato para API e web não escreverem dois
 * textos para a mesma flag.
 */
export const DICA_OPERACAO_FISCAL = {
  atualizaEstoque:
    'Ao confirmar a nota, o item gera entrada no estoque do depósito indicado. Só vale para insumo do tipo material.',
  geraFinanceiro:
    'Ao confirmar a nota, o financeiro dela vira contas a pagar e o último custo do insumo é atualizado.',
  creditaIcms:
    'O ICMS destacado é subtraído do custo de estoque. O crédito só ocorre de fato se a empresa da nota for do regime Normal — Simples e MEI não creditam nada.',
  creditaPis:
    'O PIS destacado é subtraído do custo de estoque, sujeito ao regime da empresa da nota.',
  creditaCofins:
    'O COFINS destacado é subtraído do custo de estoque, sujeito ao regime da empresa da nota.',
  consumoAutomatico:
    'Marcado, o item que usar esta operação é consumido na confirmação da nota: o material sai para a obra do rateio, no mesmo depósito em que entrou, sem lançamento de consumo à mão. Nasce marcado — na construtora o material comprado vai para a obra, e ficar no depósito é a exceção. Só tem efeito quando a operação atualiza estoque. Sozinho não obriga a informar obra: a linha sem rateio entra no depósito e fica. Quem recusa a linha sem rateio é a caixa "dispensa obra" desmarcada.',
  ignoraExigenciaObra:
    'Marcado, o item que usar esta operação não precisa de rateio por obra — nem quando a conta contábil dele exige obra. Deixe marcado nas entradas que não vão para obra nenhuma: consumo próprio, material de escritório, serviço, comodato. Desmarque nas compras de material para uso na obra, que é onde a exigência da conta contábil deve valer.',
  tipoMovimentacaoEstoqueId:
    'Motivo com que a entrada aparece no extrato do depósito. Obrigatório quando a operação atualiza estoque.',
  /*
    As da operação de **saída** (`.spec/015`, decisões 91 e 97). Texto próprio,
    e não o da entrada reaproveitado: aquele promete uma entrada no depósito ao
    confirmar a nota, e a nota de serviço emitida não movimenta estoque nenhum.
    Dizer isso na marca `?` é o que impede a caixa marcada de ser lida como
    "a emissão vai baixar o material".

    **Os três campos continuam na operação de saída, por decisão do Adriano**
    (decisão 97). A decisão 91 os tinha deixado em aberto — "Atualiza estoque"
    sem efeito, conta sugerida e dispensa de obra sem leitor —, e a proposta de
    escondê-los foi recusada, com o uso de cada um:

    - **Atualiza estoque** é da nota de material emitida — faturamento,
      devolução, transferência, por NF-e —, que ainda não existe.
    - **Conta contábil sugerida** caracteriza o tipo de faturamento, e a nota
      de serviço passou a levá-la em conta na decisão 97.
    - **Dispensa obra** servirá às notas de saída, de material e de serviço.
      Na nota de serviço ela passou a valer na decisão 98: desmarcada, a
      emissão exige a obra.

    Cada dica diz o que é verdade **hoje**, inclusive que "Atualiza estoque"
    ainda não tem efeito: prometer o que não existe faria o usuário esperar a
    baixa.
  */
  atualizaEstoqueNaSaida:
    'Na operação de saída, o movimento é uma baixa do depósito, pelo motivo escolhido abaixo. É a marca da nota de material emitida — faturamento, devolução, transferência —, que o sistema ainda não emite. A nota de serviço não movimenta estoque: nela a marca fica gravada e não tem efeito.',
  contaContabilIdNaSaida:
    'Conta de receita sugerida para a nota de serviço que escolher esta operação: a nota já vem com ela, e é por ela que o título a receber é classificado. Só sugestão — a conta pode ser trocada na nota, e a que vale é a da nota. Deixe em branco se a operação não tem uma receita natural.',
  ignoraExigenciaObraNaSaida:
    'Marcada, a nota de serviço que usar esta operação pode ser emitida com ou sem obra. Desmarcada, a emissão exige a obra: a nota fica em rascunho até uma ser escolhida. Nasce marcada — desmarque nas operações de faturamento de obra, onde nota sem obra é engano.',
  tipoMovimentacaoNaSaida:
    'Motivo com que a saída aparece no extrato do depósito. Só entram na lista os tipos de movimentação de saída. Obrigatório quando a operação atualiza estoque.',
  contaContabilId:
    'Conta contábil sugerida para o item da nota que escolher esta operação. Só sugestão: quem classifica é o item, e a classificação dele fica gravada. Deixe em branco se a operação não tem uma conta natural.',
  tributacaoIss:
    'Como o ISS incide na nota de serviço emitida com esta operação. Tributável é o caso comum; nas outras três a nota sai sem ISS.',
  retencoes:
    'Os impostos que o tomador retém na fonte. A nota de serviço só aceita valor nos que estiverem marcados, e o título a receber nasce pelo líquido — o valor do serviço menos o que foi retido.',
  geraFinanceiroNaSaida:
    'Na nota de serviço emitida, é o que faz a emissão gerar o contas a receber. Desmarcada, a nota é emitida e não nasce título nenhum.',
} as const;

/* -------------------------------------------------------------------------- */
/* Cadastro                                                                   */
/* -------------------------------------------------------------------------- */

const nomeSchema = z
  .string()
  .trim()
  .min(2, 'Informe o nome da operação fiscal.')
  .max(200, 'O nome tem no máximo 200 caracteres.');

/**
 * Vínculo opcional que vira obrigatório pela caixa acima.
 *
 * A string vazia do `<select>` entra e sai como `null`: o formulário não guarda
 * `null` em campo de controle, e sem isso "não escolhi" chegaria aqui como `''`
 * e falharia na validação de uuid com uma mensagem sobre formato.
 */
const tipoMovimentacaoEstoqueIdSchema = z
  .union([uuidSchema, z.literal(''), z.null()])
  .transform((valor) => (valor === '' || valor === null ? null : valor))
  .prefault(null);

/**
 * Conta contábil sugerida. Vazio, nulo e ausente são a mesma coisa.
 *
 * Mesma forma do vínculo acima e pelo mesmo motivo: o `<select>` devolve `''`
 * quando nada foi escolhido, e sem a união isso falharia com uma mensagem sobre
 * formato de uuid — num campo opcional.
 *
 * **Sem `superRefine` ligando-a a `geraFinanceiro`**, e a ausência é deliberada:
 * a conta aqui é sugestão, e a operação que gera financeiro sem sugerir conta
 * apenas obriga quem digita a nota a classificar a linha na mão. Exigi-la no
 * cadastro travaria a operação fiscal por uma decisão que é do contador e pode
 * variar de item para item.
 */
const contaContabilIdSchema = z
  .union([uuidSchema, z.literal(''), z.null()])
  .transform((valor) => (valor === '' || valor === null ? null : valor))
  .prefault(null);

/**
 * Payload de gravação.
 *
 * **Não há `credita_ipi`** — decisão de `.spec/001`, seção 2.2 (c): crédito de
 * IPI cabe a estabelecimento industrial, e o IPI da nota de construtora soma ao
 * custo em vez de se recuperar. A flag existiria só para ficar sempre
 * desmarcada, e uma caixa que nunca se marca é convite a marcá-la por engano.
 *
 * O `superRefine` aponta o erro em `tipoMovimentacaoEstoqueId`, e não em
 * `atualizaEstoque`: quem está incompleto é o campo em branco. Apontar a caixa
 * mandaria desmarcar a decisão que o usuário acabou de tomar.
 */
export const criarOperacaoFiscalSchema = z
  .object({
    nome: nomeSchema,
    /**
     * Obrigatório na operação de entrada, opcional na de saída (decisão 97).
     * A regra é do `superRefine`, por `problemaDoCfop`.
     */
    cfop: cfopOpcionalSchema,
    /**
     * Entrada ou saída.
     *
     * **Opcional quando há CFOP** — o primeiro dígito já diz, e o payload de
     * antes da decisão 97, que não mandava o campo, continua valendo. Sem
     * CFOP é ele quem diz, e só `SAIDA` é aceito.
     *
     * Até 07/10/2026 este campo não existia no payload, de propósito: "o
     * primeiro dígito do CFOP é a regra inteira". Deixou de ser quando o CFOP
     * deixou de ser obrigatório.
     */
    sentido: sentidoOperacaoSchema.optional(),

    atualizaEstoque: z.boolean().prefault(false),
    geraFinanceiro: z.boolean().prefault(false),

    /**
     * Os três nascem **desmarcados**, e o padrão é deliberado (`.spec/001`,
     * seção 2.2 (c)): operação cadastrada sem se pensar no crédito não credita.
     * Esquecer de marcar superavalia o custo, o que aparece na conferência; o
     * erro inverso subavalia em silêncio, e ninguém procura um custo baixo.
     */
    creditaIcms: z.boolean().prefault(false),
    creditaPis: z.boolean().prefault(false),
    creditaCofins: z.boolean().prefault(false),

    /**
     * Dispensa o rateio por obra que a conta contábil do item exigiria.
     *
     * **Nasce marcada**, e é o único padrão invertido do payload. A maioria das
     * operações de uma construtora não tem obra a apontar — serviço, comodato,
     * devolução, ativo, transporte, energia —, e a exigência herdada da conta
     * travaria a primeira nota de cada uma numa obra inventada. Quem compra
     * material **para uso na obra** desmarca, e aí a exigência da conta volta a
     * valer para aquela operação.
     *
     * **Sem `superRefine` ligando-a a `atualizaEstoque` ou a `geraFinanceiro`**:
     * a combinação improvável — dispensar obra numa compra de material — é
     * legítima na empresa que compra material para o estoque da administração, e
     * recusá-la aqui obrigaria a cadastrar uma operação que não existe.
     */
    ignoraExigenciaObra: z.boolean().prefault(true),

    /**
     * O item que usar esta operação é consumido na confirmação da nota.
     *
     * **Nasce marcada**, e é o segundo padrão ligado do payload (`.spec/015`,
     * decisão 50). A flag morava no vínculo do insumo com a empresa e veio para
     * cá porque consumir ao chegar descreve o que aquela **entrada** significa,
     * e não o material: o mesmo cimento vai para a obra numa nota e para o
     * almoxarifado central na outra.
     *
     * **Sem `superRefine` ligando-a a `atualizaEstoque`**: quem não movimenta
     * estoque não tem o que consumir, e a flag fica inerte — a confirmação só a
     * lê depois de `atualiza_estoque`. Recusá-la aqui obrigaria a desmarcar uma
     * caixa que não faz nada em serviço, comodato e devolução.
     *
     * **Nem com `ignoraExigenciaObra`**: consumo automático numa operação que
     * dispensa a obra é combinação legítima e comum — a linha rateada consome, a
     * não rateada entra no depósito e fica. É a mesma compra que às vezes vai
     * para a obra e às vezes para o almoxarifado central.
     */
    consumoAutomatico: z.boolean().prefault(true),

    tipoMovimentacaoEstoqueId: tipoMovimentacaoEstoqueIdSchema,
    contaContabilId: contaContabilIdSchema,

    /**
     * Os CFOPs de saída do fornecedor que caem nesta operação (decisão 21).
     *
     * **Filhos no mesmo payload**, regravados por inteiro no `PUT`, como manda o
     * `CLAUDE.md`. Uma lista vazia é legítima: a operação que não recebe nota por
     * XML — serviço, ou uma criada à mão para lançamento manual — não tem origem
     * a declarar.
     */
    cfopsOrigem: cfopsOrigemSchema,

    /*
      Os impostos da **nota de serviço emitida** (`.spec/015`, decisão 90).

      A operação fiscal já dizia, na nota de compra, se há financeiro e quais
      impostos creditam; na nota de serviço ela diz se há financeiro e quais
      impostos **se aplicam**. É a mesma pergunta do outro lado do balcão, e é
      por isso que mora no mesmo cadastro em vez de num "tipo de nota de
      serviço" à parte.

      Só fazem sentido em operação de **saída**. Na de entrada ficam no padrão e
      são recusadas se vierem marcadas — ver o `superRefine`.

      Todas nascem desligadas, e `TRIBUTAVEL` é o padrão do ISS: é o caso da
      empreitada comum, e a operação cadastrada sem se pensar em retenção não
      retém nada.
    */
    tributacaoIss: tributacaoIssSchema.prefault('TRIBUTAVEL'),
    /** O ISS é retido pelo tomador — quem paga a guia é ele. */
    retemIss: z.boolean().prefault(false),
    retemIrrf: z.boolean().prefault(false),
    retemInss: z.boolean().prefault(false),
    retemCsll: z.boolean().prefault(false),
    retemPis: z.boolean().prefault(false),
    retemCofins: z.boolean().prefault(false),

    ativo: z.boolean().prefault(true),
  })
  .superRefine((dados, ctx) => {
    /*
      O CFOP e o sentido (decisão 97). Apontado em `cfop` nos dois casos: é o
      campo que se digita, e o sentido da tela acompanha o que se digita nele.

      As recusas abaixo que dependem do sentido usam o **resolvido**, e ficam
      caladas quando ele não se resolve — o erro que importa já foi dado aqui,
      e um segundo sobre CFOP de origem só confundiria.
    */
    const problema = problemaDoCfop(dados);
    if (problema !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cfop'],
        message: MENSAGEM_DO_PROBLEMA_DO_CFOP[problema],
      });
    }

    const sentido = sentidoDaOperacao(dados);

    if (!tipoMovimentacaoCoerente(dados)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['tipoMovimentacaoEstoqueId'],
        // "O movimento", e não "a entrada": desde a decisão 91 a operação de
        // saída aponta um motivo de saída, e a frase vale para as duas.
        message:
          'Escolha o tipo de movimentação: o movimento de estoque gerado por esta operação precisa de um motivo para aparecer no extrato do depósito.',
      });
    }

    /*
      Só operação de **entrada** tem origem.

      A devolução ao fornecedor é saída: quem emite a nota é a própria conta, e
      não há CFOP de emitente a mapear. Aceitar a lista ali criaria um cadastro
      que nunca é lido, e o usuário só descobriria isso não vendo a sugestão
      aparecer.
    */
    if (dados.cfopsOrigem.length > 0 && sentido === 'SAIDA') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cfopsOrigem'],
        message:
          'Só operação de entrada tem CFOP de origem: a de saída é emitida por você, e não tem emitente a mapear.',
      });
    }

    /*
      Retenção e tributação do ISS são da nota que a conta **emite**.

      Na entrada quem retém é a conta, sobre o serviço que comprou, e isso é
      campo do item da nota de compra — não marca de operação. Aceitar as marcas
      aqui criaria um cadastro que nenhuma tela lê.
    */
    if (sentido === 'ENTRADA' && impostosDeSaidaInformados(dados)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['tributacaoIss'],
        message:
          'Tributação do ISS e retenções são da nota de serviço emitida: só operação de saída as tem.',
      });
    }

    // O único do banco recusaria a repetição com erro de constraint. Aqui a
    // recusa chega presa ao campo, com o motivo.
    if (temOrigemRepetida(dados.cfopsOrigem)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cfopsOrigem'],
        message: 'Há CFOP de origem repetido na lista.',
      });
    }
  });

export type CriarOperacaoFiscal = z.infer<typeof criarOperacaoFiscalSchema>;

export const atualizarOperacaoFiscalSchema = criarOperacaoFiscalSchema;
export type AtualizarOperacaoFiscal = z.infer<typeof atualizarOperacaoFiscalSchema>;

/**
 * Leitura.
 *
 * `tipoMovimentacaoEstoqueNome` vem desnormalizado do `leftJoin`: a listagem
 * mostra o motivo por extenso, e resolvê-lo no cliente custaria uma consulta por
 * linha.
 */
export const operacaoFiscalSchema = z.object({
  id: uuidSchema,
  nome: z.string(),
  /** `null` na operação de saída cadastrada sem CFOP (decisão 97). */
  cfop: z.string().nullable(),
  /**
   * ENTRADA ou SAIDA — o do CFOP, ou o declarado quando não há CFOP. Ver
   * `sentidoDaOperacao`.
   */
  sentido: sentidoOperacaoSchema,
  atualizaEstoque: z.boolean(),
  geraFinanceiro: z.boolean(),
  creditaIcms: z.boolean(),
  creditaPis: z.boolean(),
  creditaCofins: z.boolean(),
  /** Dispensa o rateio por obra exigido pela conta contábil do item. */
  ignoraExigenciaObra: z.boolean(),
  /** Consome o item na confirmação da nota, para a obra do rateio da linha. */
  consumoAutomatico: z.boolean(),
  tipoMovimentacaoEstoqueId: uuidSchema.nullable(),
  tipoMovimentacaoEstoqueNome: z.string().nullable(),
  contaContabilId: uuidSchema.nullable(),
  /** Descrição da conta sugerida, desnormalizada — a listagem mostra o nome. */
  contaContabilDescricao: z.string().nullable(),
  /**
   * Os CFOPs de saída do fornecedor que caem nesta operação.
   *
   * Viajam **também na listagem**, e não só no `GET` por id. É o que permite à
   * entrada por XML montar o de‑para inteiro com a lista que ela já carrega para
   * encher o seletor, sem consulta a mais — o cadastro é curto, e o custo é uma
   * coluna agregada.
   */
  cfopsOrigem: z.array(z.string()),
  tributacaoIss: tributacaoIssSchema,
  retemIss: z.boolean(),
  retemIrrf: z.boolean(),
  retemInss: z.boolean(),
  retemCsll: z.boolean(),
  retemPis: z.boolean(),
  retemCofins: z.boolean(),
  ativo: z.boolean(),
  createdAt: dataHoraSchema,
  updatedAt: dataHoraSchema,
});

export type OperacaoFiscal = z.infer<typeof operacaoFiscalSchema>;

export const listaOperacoesFiscaisSchema = respostaListaSchema(operacaoFiscalSchema);
export type ListaOperacoesFiscais = z.infer<typeof listaOperacoesFiscaisSchema>;

/**
 * Filtro booleano da query string.
 *
 * `z.coerce.boolean()` **não serve**: toda string não vazia vira `true`, e
 * `?atualizaEstoque=false` traria justamente as que atualizam. É o mesmo motivo
 * de `filtroAtivoSchema` em `comum.ts`.
 */
const filtroBooleanoSchema = z
  .union([z.boolean(), z.enum(['true', 'false'])])
  .transform((v) => v === true || v === 'true')
  .optional();

/**
 * Listagem, com dois filtros além dos comuns.
 *
 * `atualizaEstoque` porque "quais operações mexem no estoque?" é a pergunta que
 * se faz ao conferir por que uma nota confirmada não apareceu no depósito. O
 * campo de busca não responde: o nome não diz se a caixa está marcada.
 */
export const listarOperacoesFiscaisQuerySchema = listagemDeCadastroSchema.extend({
  atualizaEstoque: filtroBooleanoSchema,
  geraFinanceiro: filtroBooleanoSchema,
  /**
   * Recorte por sentido.
   *
   * É o filtro da nota de compra (`ENTRADA` — `.spec/015`, decisão 92) e o da
   * nota de serviço emitida (`SAIDA` — decisão 90). Ausente traz os dois, que é
   * o uso do cadastro: quem confere o catálogo quer ver as operações de saída
   * ao lado das compras.
   */
  sentido: sentidoOperacaoSchema.optional(),
});

export type ListarOperacoesFiscaisQuery = z.infer<typeof listarOperacoesFiscaisQuerySchema>;

/**
 * Colunas de ordenação.
 *
 * A ordem padrão é `nome`, crescente: o cadastro é curto e se procura pelo nome.
 * `cfop` entra porque a conferência fiscal percorre a lista pelo código, não
 * pela descrição.
 */
export const COLUNAS_ORDENAVEIS_OPERACAO_FISCAL = ['nome', 'cfop', 'createdAt'] as const;
